import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Space, Table, message, Divider, Row, Col, 
  InputNumber, Progress, Input, Select, Modal, Form, Tag, Alert, Checkbox
} from 'antd';
import { 
  CopyOutlined, DownloadOutlined, WalletOutlined, SendOutlined,
  ReloadOutlined, StopOutlined, DeleteOutlined
} from '@ant-design/icons';
import { ethers } from 'ethers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// 接口定义
interface ImportedWallet {
  address: string;
  privateKey: string;
  balance?: string;
  allowance?: string;
  status?: 'idle' | 'pending' | 'success' | 'failed' | 'stopped';
  balanceLoading?: boolean;
  allowanceLoading?: boolean;
}

interface NetworkConfig {
  name: string;
  rpcUrl: string;
  chainId: number;
  symbol: string;
}

// 预设网络配置
const NETWORK_CONFIGS: NetworkConfig[] = [
  {
    name: 'BNB Smart Chain Mainnet',
    rpcUrl: 'https://bsc-dataseed2.bnbchain.org',
    chainId: 56,
    symbol: 'BNB'
  },
  {
    name: 'Ethereum Mainnet',
    rpcUrl: 'https://eth.llamarpc.com',
    chainId: 1,
    symbol: 'ETH'
  },
  {
    name: 'Polygon Mainnet',
    rpcUrl: 'https://polygon-rpc.com',
    chainId: 137,
    symbol: 'MATIC'
  },
  {
    name: 'Arbitrum One',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    chainId: 42161,
    symbol: 'ETH'
  },
  {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    chainId: 8453,
    symbol: 'ETH'
  },
  {
    name: 'Optimism',
    rpcUrl: 'https://mainnet.optimism.io',
    chainId: 10,
    symbol: 'ETH'
  },
  {
    name: 'Avalanche',
    rpcUrl: 'https://api.avax.network/ext/bc/C/rpc',
    chainId: 43114,
    symbol: 'AVAX'
  },
  {
    name: 'Fantom',
    rpcUrl: 'https://rpc.fantom.network',
    chainId: 250,
    symbol: 'FTM'
  },
];

// 常见ERC20代币配置 - 按链分类
const COMMON_TOKENS = [
  // BSC (Chain ID: 56)
  { symbol: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955', chainId: 56, chainName: 'BSC' },
  { symbol: 'USDC', address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', chainId: 56, chainName: 'BSC' },
  { symbol: 'USD1', address: '0x8d0D000Ee44948FC98c9B98A4FA4921476f08B0d', chainId: 56, chainName: 'BSC' },
  { symbol: 'WBNB', address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', chainId: 56, chainName: 'BSC' },
  { symbol: 'DAI', address: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3', chainId: 56, chainName: 'BSC' },
  
  // Ethereum (Chain ID: 1)
  { symbol: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', chainId: 1, chainName: 'ETH' },
  { symbol: 'USDC', address: '0xA0b86a33E6741b8f4ba22a4e6f8f7AdF576c84173', chainId: 1, chainName: 'ETH' },
  { symbol: 'DAI', address: '0x6B175474E89094C44C9F6B5B73C165C93B9E46B5', chainId: 1, chainName: 'ETH' },
  
  // Polygon (Chain ID: 137)
  { symbol: 'USDT', address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', chainId: 137, chainName: 'MATIC' },
  { symbol: 'USDC', address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', chainId: 137, chainName: 'MATIC' },
  { symbol: 'DAI', address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', chainId: 137, chainName: 'MATIC' },

  // Base (Chain ID: 8453)
  { symbol: 'USDC', address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', chainId: 8453, chainName: 'Base' },
  // Arbitrum (Chain ID: 42161)
  { symbol: 'USDT', address: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9', chainId: 42161, chainName: 'Arbitrum' },
  { symbol: 'USDC', address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', chainId: 42161, chainName: 'Arbitrum' },
  { symbol: 'DAI', address: '0x4671947bA2237976c65bF69d674A3D73039d6C65', chainId: 42161, chainName: 'Arbitrum' },
  // Optimism (Chain ID: 10)
  { symbol: 'USDC', address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', chainId: 10, chainName: 'Optimism' },
];
// ERC20 ABI (精简版)
const ERC20_ABI = [
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address account) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)"
];

const BatchApproval: React.FC = () => {
  // 状态管理
  const [selectedNetwork, setSelectedNetwork] = useState<string>('BNB Smart Chain Mainnet');
  const [customRpc, setCustomRpc] = useState<string>('https://bsc-dataseed2.bnbchain.org');
  const [tokenContract, setTokenContract] = useState<string>('');
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [spenderAddress, setSpenderAddress] = useState<string>('');
  const [approvalAmount, setApprovalAmount] = useState<string>('');
  const [customGas, setCustomGas] = useState<string>('');
  const [delay, setDelay] = useState<number>(0.5);
  
  // 钱包相关状态
  const [wallets, setWallets] = useState<ImportedWallet[]>([]);
  const [selectedWallets, setSelectedWallets] = useState<string[]>([]);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [privateKeysInput, setPrivateKeysInput] = useState<string>('');
  
  // 执行状态
  const [loading, setLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);
  
  // Token信息
  const [tokenSymbol, setTokenSymbol] = useState<string>('');
  const [tokenDecimals, setTokenDecimals] = useState<number>(18);

  // 统一的组件样式
  const componentStyle = {
    height: '36px',
    borderRadius: '6px',
  };

  const buttonStyle = {
    ...componentStyle,
    minWidth: '80px',
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    color: 'white',
  };

  // 添加日志
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleString('zh-CN');
    const logMessage = `${timestamp} ${message}`;
    setLogs(prev => [...prev, logMessage]);
  };

  // 获取当前网络配置
  const getCurrentNetworkConfig = (): NetworkConfig => {
    return NETWORK_CONFIGS.find(config => config.name === selectedNetwork) || NETWORK_CONFIGS[0];
  };

  // 获取当前RPC URL
  const getCurrentRpcUrl = (): string => {
    if (selectedNetwork === 'Custom') {
      return customRpc;
    }
    return getCurrentNetworkConfig().rpcUrl;
  };

  // 获取当前网络对应的代币列表
  const getCurrentNetworkTokens = () => {
    const currentNetwork = getCurrentNetworkConfig();
    return COMMON_TOKENS.filter(token => token.chainId === currentNetwork.chainId);
  };

  // 验证地址格式
  const isValidAddress = (address: string): boolean => {
    try {
      return ethers.isAddress(address);
    } catch {
      return false;
    }
  };

  // 网络切换处理
  const handleNetworkChange = (value: string) => {
    setSelectedNetwork(value);
    if (value !== 'Custom') {
      const config = NETWORK_CONFIGS.find(config => config.name === value);
      if (config) {
        setCustomRpc(config.rpcUrl);
      }
    }
    
    // 检查当前选择的代币是否属于新网络，如果不匹配则清空选择
    if (selectedToken && selectedToken !== 'custom') {
      const newNetworkConfig = NETWORK_CONFIGS.find(config => config.name === value);
      if (newNetworkConfig) {
        const tokenExistsInNewNetwork = COMMON_TOKENS.some(
          token => token.symbol === selectedToken && token.chainId === newNetworkConfig.chainId
        );
        if (!tokenExistsInNewNetwork) {
          setSelectedToken('');
          setTokenContract('');
          setTokenSymbol('');
          setTokenDecimals(18);
        }
      }
    }
  };

  // Token选择处理
  const handleTokenSelect = (value: string) => {
    setSelectedToken(value);
    if (value !== 'custom') {
      const currentNetwork = getCurrentNetworkConfig();
      const token = COMMON_TOKENS.find(t => t.symbol === value && t.chainId === currentNetwork.chainId);
      if (token) {
        setTokenContract(token.address);
      }
    } else {
      setTokenContract('');
    }
  };

  // 导入钱包
  const handleImportWallets = () => {
    if (!privateKeysInput.trim()) {
      message.error('请输入私钥');
      return;
    }

    const privateKeys = privateKeysInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const newWallets: ImportedWallet[] = [];
    const invalidKeys: string[] = [];

    privateKeys.forEach((privateKey, index) => {
      try {
        const wallet = new ethers.Wallet(privateKey);
        newWallets.push({
          address: wallet.address,
          privateKey: privateKey,
          status: 'idle'
        });
      } catch (error) {
        invalidKeys.push(`第${index + 1}行`);
      }
    });

    if (invalidKeys.length > 0) {
      message.error(`发现${invalidKeys.length}个无效私钥: ${invalidKeys.join(', ')}`);
      return;
    }

    setWallets(newWallets);
    setSelectedWallets(newWallets.map(w => w.address));
    setPrivateKeysInput('');
    setIsImportModalVisible(false);
    
    addLog(`成功导入${newWallets.length}个钱包`);
    message.success(`成功导入${newWallets.length}个钱包`);

    // 自动查询余额
    setTimeout(() => {
      refreshData();
    }, 1000);
  };

  // 检查Token信息
  const checkTokenInfo = async () => {
    if (!tokenContract || !isValidAddress(tokenContract)) {
      setTokenSymbol('');
      setTokenDecimals(18);
      return;
    }

    try {
      const rpcUrl = getCurrentRpcUrl();
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const tokenContractInstance = new ethers.Contract(tokenContract, ERC20_ABI, provider);

      const [symbol, decimals] = await Promise.all([
        tokenContractInstance.symbol(),
        tokenContractInstance.decimals()
      ]);

      setTokenSymbol(symbol);
      setTokenDecimals(Number(decimals));
      addLog(`Token信息已更新: ${symbol} (${decimals}位小数)`);
    } catch (error) {
      console.error('获取Token信息失败:', error);
      setTokenSymbol('');
      setTokenDecimals(18);
    }
  };

  // 监听Token合约地址变化
  useEffect(() => {
    if (tokenContract && isValidAddress(tokenContract)) {
      checkTokenInfo();
    }
  }, [tokenContract]);

  // 刷新数据
  const refreshData = async () => {
    if (wallets.length === 0) {
      message.warning('请先导入钱包');
      return;
    }

    setLoading(true);
    addLog('开始刷新钱包数据...');

    try {
      const rpcUrl = getCurrentRpcUrl();
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const networkConfig = getCurrentNetworkConfig();

      // 更新钱包状态为加载中
      const updatedWallets = wallets.map(wallet => ({
        ...wallet,
        balanceLoading: true,
        allowanceLoading: tokenContract && spenderAddress ? true : false
      }));
      setWallets(updatedWallets);

      // 并发查询余额和授权额度
      const promises = wallets.map(async (wallet, index) => {
        try {
          // 查询主币余额
          const balance = await provider.getBalance(wallet.address);
          const balanceFormatted = ethers.formatEther(balance);

          let allowance = '';
          let allowanceFormatted = '';

          // 如果有Token合约和授权地址，查询授权额度
          if (tokenContract && spenderAddress && isValidAddress(tokenContract) && isValidAddress(spenderAddress)) {
            try {
              const tokenContractInstance = new ethers.Contract(tokenContract, ERC20_ABI, provider);
              const allowanceResult = await tokenContractInstance.allowance(wallet.address, spenderAddress);
              allowanceFormatted = ethers.formatUnits(allowanceResult, tokenDecimals);
            } catch (error) {
              console.error(`查询授权额度失败 ${wallet.address}:`, error);
              allowanceFormatted = 'N/A';
            }
          }

          return {
            ...wallet,
            balance: balanceFormatted,
            allowance: allowanceFormatted,
            balanceLoading: false,
            allowanceLoading: false
          };
        } catch (error) {
          console.error(`查询失败 ${wallet.address}:`, error);
          return {
            ...wallet,
            balance: 'Error',
            allowance: 'Error',
            balanceLoading: false,
            allowanceLoading: false
          };
        }
      });

      const results = await Promise.all(promises);
      setWallets(results);

      addLog(`数据刷新完成，共更新${results.length}个钱包`);
      message.success('数据刷新完成');
    } catch (error) {
      console.error('刷新数据失败:', error);
      message.error('刷新数据失败');
      addLog('数据刷新失败');
    } finally {
      setLoading(false);
    }
  };

  // 执行授权
  const executeApproval = async () => {
    // 验证输入
    if (selectedWallets.length === 0) {
      message.error('请选择要执行授权的钱包');
      return;
    }

    if (!tokenContract || !isValidAddress(tokenContract)) {
      message.error('请输入有效的Token合约地址');
      return;
    }

    if (!spenderAddress || !isValidAddress(spenderAddress)) {
      message.error('请输入有效的授权给地址');
      return;
    }

    setIsExecuting(true);
    setProgress(0);
    setCurrentStep('准备执行授权...');
    addLog(`开始批量授权，共${selectedWallets.length}个钱包`);

    try {
      const rpcUrl = getCurrentRpcUrl();
      const provider = new ethers.JsonRpcProvider(rpcUrl);

      // 计算授权数量
      let approvalAmountWei;
      if (!approvalAmount || approvalAmount.trim() === '') {
        // 无限授权
        approvalAmountWei = ethers.MaxUint256;
        addLog('使用无限授权额度');
      } else {
        approvalAmountWei = ethers.parseUnits(approvalAmount, tokenDecimals);
        addLog(`授权数量: ${approvalAmount} ${tokenSymbol}`);
      }

      const selectedWalletData = wallets.filter(w => selectedWallets.includes(w.address));
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < selectedWalletData.length; i++) {
        const wallet = selectedWalletData[i];
        const progressPercent = Math.round(((i + 1) / selectedWalletData.length) * 100);

        setProgress(progressPercent);
        setCurrentStep(`正在为 ${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)} 执行授权 (${i + 1}/${selectedWalletData.length})`);

        // 更新钱包状态为处理中
        setWallets(prev => prev.map(w =>
          w.address === wallet.address ? { ...w, status: 'pending' } : w
        ));

        try {
          // 创建钱包实例
          const walletInstance = new ethers.Wallet(wallet.privateKey, provider);
          const tokenContractInstance = new ethers.Contract(tokenContract, ERC20_ABI, walletInstance);

          // 构建交易参数
          const txParams: any = {
            gasLimit: customGas ? BigInt(customGas) : undefined
          };

          addLog(`正在为 ${wallet.address} 发送授权交易...`);

          // 发送授权交易
          const tx = await tokenContractInstance.approve(spenderAddress, approvalAmountWei, txParams);
          addLog(`交易已发送，哈希: ${tx.hash}`);

          // 等待交易确认
          const receipt = await tx.wait();

          if (receipt.status === 1) {
            successCount++;
            addLog(`✅ ${wallet.address} 授权成功，交易哈希: ${tx.hash}`);

            // 更新钱包状态为成功
            setWallets(prev => prev.map(w =>
              w.address === wallet.address ? { ...w, status: 'success' } : w
            ));

            // 重新查询授权额度
            try {
              const tokenQuery = new ethers.Contract(tokenContract, ERC20_ABI, provider);
              const newAllowance = await tokenQuery.allowance(wallet.address, spenderAddress);
              const newAllowanceFormatted = ethers.formatUnits(newAllowance, tokenDecimals);

              setWallets(prev => prev.map(w =>
                w.address === wallet.address ? { ...w, allowance: newAllowanceFormatted } : w
              ));
            } catch (error) {
              console.error('更新授权额度失败:', error);
            }
          } else {
            throw new Error('交易失败');
          }
        } catch (error: any) {
          failedCount++;
          const errorMessage = error.message || '未知错误';
          addLog(`❌ ${wallet.address} 授权失败: ${errorMessage}`);

          // 更新钱包状态为失败
          setWallets(prev => prev.map(w =>
            w.address === wallet.address ? { ...w, status: 'failed' } : w
          ));
        }

        // 延迟
        if (i < selectedWalletData.length - 1 && delay > 0) {
          setCurrentStep(`等待 ${delay} 秒后继续...`);
          await new Promise(resolve => setTimeout(resolve, delay * 1000));
        }
      }

      setProgress(100);
      setCurrentStep('授权完成');
      addLog(`批量授权完成！成功: ${successCount}个，失败: ${failedCount}个`);

      if (successCount > 0) {
        message.success(`授权完成！成功: ${successCount}个，失败: ${failedCount}个`);
      } else {
        message.error('所有授权都失败了，请检查网络和参数设置');
      }
    } catch (error) {
      console.error('批量授权失败:', error);
      message.error('批量授权失败');
      addLog('批量授权过程中发生错误');
    } finally {
      setIsExecuting(false);
      setTimeout(() => {
        setProgress(0);
        setCurrentStep('');
      }, 3000);
    }
  };

  // 停止任务
  const stopTask = () => {
    setIsExecuting(false);
    setProgress(0);
    setCurrentStep('');
    addLog('任务已手动停止');
    message.warning('任务已停止');

    // 将所有pending状态的钱包设置为stopped
    setWallets(prev => prev.map(w =>
      w.status === 'pending' ? { ...w, status: 'stopped' } : w
    ));
  };

  // 导出结果到Excel
  const exportToExcel = () => {
    if (wallets.length === 0) {
      message.warning('没有数据可导出');
      return;
    }

    const exportData = wallets.map((wallet, index) => ({
      '序号': index + 1,
      '钱包地址': wallet.address,
      [`${getCurrentNetworkConfig().symbol}余额`]: wallet.balance || 'N/A',
      '授权状态': {
        idle: '未开始',
        pending: '处理中',
        success: '成功',
        failed: '失败',
        stopped: '已停止',
      }[wallet.status || 'idle'],
      '授权额度': wallet.allowance || 'N/A',
      'Token合约': tokenContract,
      '授权给地址': spenderAddress,
      '网络': selectedNetwork,
      'RPC': getCurrentRpcUrl(),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '批量授权结果');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    saveAs(data, `批量授权结果_${timestamp}.xlsx`);

    addLog('授权结果已导出到Excel文件');
    message.success('导出成功');
  };

  return (
    <div className="container-responsive">
      {/* 标题 */}
      <h1 style={{ 
        textAlign: 'center', 
        fontSize: 'var(--font-xl)',
        fontWeight: 'bold',
        marginBottom: 'var(--spacing-2xl)',
        color: '#333'
      }}>
        批量ERC20代币授权管理
      </h1>

      {/* 顶部操作按钮 */}
      <Card className="spacing-responsive">
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={refreshData}
            loading={loading}
            style={buttonStyle}
          >
            刷新数据
          </Button>
          <Button
            icon={<WalletOutlined />}
            onClick={() => setIsImportModalVisible(true)}
            style={buttonStyle}
          >
            导入钱包
          </Button>

          {wallets.length > 0 && (
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              style={buttonStyle}
            >
              导出结果
            </Button>
          )}
        </Space>
      </Card>

      {/* 网络设置 */}
      <Card 
        // title="网络设置" 
        className="spacing-responsive"
        style={{ padding: '16px' }}
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                选择网络
              </h3>
              <Select
                value={selectedNetwork}
                onChange={handleNetworkChange}
                style={{ ...componentStyle, width: '100%' }}
                options={[
                  ...NETWORK_CONFIGS.map(config => ({
                    label: config.name,
                    value: config.name
                  })),
                  { label: '自定义', value: 'Custom' }
                ]}
              />
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                自定义RPC
              </h3>
              <Input
                value={customRpc}
                onChange={(e) => {
                  setCustomRpc(e.target.value);
                  if (selectedNetwork !== 'Custom') {
                    setSelectedNetwork('Custom');
                  }
                }}
                placeholder="输入自定义RPC URL"
                style={componentStyle}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {/* 授权设置 */}
      <Card 
        // title="授权设置" 
        className="spacing-responsive"
        style={{ padding: '16px' }}
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                要用Token合约
              </h3>
              <Space.Compact style={{ width: '100%' }}>
                <Select
                  value={selectedToken}
                  onChange={handleTokenSelect}
                  style={{ ...componentStyle, width: '30%' }}
                  placeholder="选择Token"
                  options={[
                    { label: '自定义', value: 'custom' },
                    ...getCurrentNetworkTokens().map(token => ({
                      label: `${token.symbol} (${token.chainName})`,
                      value: token.symbol
                    }))
                  ]}
                />
                <Input
                  value={tokenContract}
                  onChange={(e) => setTokenContract(e.target.value)}
                  placeholder="输入Token合约地址"
                  style={{ ...componentStyle, width: '70%' }}
                />
              </Space.Compact>
              {tokenSymbol && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#1890ff' }}>
                  Token: {tokenSymbol} ({tokenDecimals}位小数)
                </div>
              )}
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                授权给地址
              </h3>
              <Input
                value={spenderAddress}
                onChange={(e) => setSpenderAddress(e.target.value)}
                placeholder="填写要授权给的地址 (请确保该地址安全)"
                style={componentStyle}
              />
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
                这是将被授权使用您Token的地址
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                授权数量
              </h3>
              <Input
                value={approvalAmount}
                onChange={(e) => setApprovalAmount(e.target.value)}
                placeholder="填写授权数量"
                style={componentStyle}
              />
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#999' }}>
                留空表示无限授权
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                自定义Gas (可选)
              </h3>
              <Input
                value={customGas}
                onChange={(e) => setCustomGas(e.target.value)}
                placeholder="自定义gas 可选"
                style={componentStyle}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div>
              <h3 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                延迟(秒)
              </h3>
              <InputNumber
                value={delay}
                onChange={(value) => setDelay(value || 0.5)}
                min={0}
                step={0.1}
                style={{ ...componentStyle, width: '100%' }}
                placeholder="0.5"
              />
            </div>
          </Col>
        </Row>

        {/* 执行按钮区域 */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <Space size="large">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={executeApproval}
              disabled={selectedWallets.length === 0 || isExecuting}
              loading={isExecuting}
              style={primaryButtonStyle}
              size="large"
            >
              执行授权
            </Button>
            {isExecuting && (
              <Button
                icon={<StopOutlined />}
                onClick={stopTask}
                danger
                style={buttonStyle}
                size="large"
              >
                停止任务
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* 钱包列表 */}
      {wallets.length > 0 && (
        <>
          <Divider>
            <span style={{ color: '#1890ff', fontSize: '16px' }}>
              钱包列表 - 总选中{selectedWallets.length}个，已授权{wallets.filter(w => w.status === 'success').length}个
            </span>
          </Divider>

          <Table
            columns={[
              {
                title: (
                  <Checkbox
                    checked={selectedWallets.length === wallets.length}
                    indeterminate={selectedWallets.length > 0 && selectedWallets.length < wallets.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWallets(wallets.map(w => w.address));
                      } else {
                        setSelectedWallets([]);
                      }
                    }}
                  />
                ),
                dataIndex: 'selected',
                width: 50,
                render: (_, record) => (
                  <Checkbox
                    checked={selectedWallets.includes(record.address)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWallets([...selectedWallets, record.address]);
                      } else {
                        setSelectedWallets(selectedWallets.filter(addr => addr !== record.address));
                      }
                    }}
                  />
                ),
              },
              {
                title: '序号',
                dataIndex: 'index',
                width: 60,
                render: (_, __, index) => index + 1,
              },
              {
                title: '地址',
                dataIndex: 'address',
                width: 200,
                render: (address) => (
                  <Space>
                    <span>{`${address.slice(0, 6)}...${address.slice(-4)}`}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(address);
                        message.success('地址已复制');
                      }}
                    />
                  </Space>
                ),
              },
              {
                title: `${getCurrentNetworkConfig().symbol}余额`,
                dataIndex: 'balance',
                width: 120,
                render: (balance, record) => (
                  <span>
                    {record.balanceLoading ? '查询中...' : balance ? `${parseFloat(balance).toFixed(4)} ${getCurrentNetworkConfig().symbol}` : 'N/A'}
                  </span>
                ),
              },
              {
                title: '授权状态',
                dataIndex: 'status',
                width: 100,
                render: (status) => {
                  const statusConfig = {
                    idle: { color: 'default', text: '未开始' },
                    pending: { color: 'processing', text: '处理中' },
                    success: { color: 'success', text: '成功' },
                    failed: { color: 'error', text: '失败' },
                    stopped: { color: 'warning', text: '已停止' },
                  };
                  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.idle;
                  return <Tag color={config.color}>{config.text}</Tag>;
                },
              },
              {
                title: '授权额度',
                dataIndex: 'allowance',
                width: 150,
                render: (allowance, record) => (
                  <span>
                    {record.allowanceLoading ? '查询中...' : allowance ? `${parseFloat(allowance || '0').toFixed(4)} ${tokenSymbol || 'Token'}` : 'N/A'}
                  </span>
                ),
              },
              {
                title: '操作',
                width: 80,
                render: (_, record) => (
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setWallets(wallets.filter(w => w.address !== record.address));
                      setSelectedWallets(selectedWallets.filter(addr => addr !== record.address));
                      message.success('钱包已移除');
                    }}
                    danger
                  />
                ),
              },
            ]}
            dataSource={wallets}
            rowKey="address"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 个钱包`,
            }}
            scroll={{ x: 800 }}
            size="small"
          />
        </>
      )}

      {/* 执行进度 */}
      {isExecuting && (
        <Card title="执行进度" className="spacing-responsive">
          <Progress
            percent={progress}
            status="active"
            style={{ marginBottom: '16px' }}
          />
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            {currentStep}
          </div>
        </Card>
      )}

      {/* 执行日志 */}
      {logs.length > 0 && (
        <Card title="执行日志" className="spacing-responsive">
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '6px',
            maxHeight: '300px',
            overflowY: 'auto',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap'
          }}>
            {logs.join('\n')}
          </div>
        </Card>
      )}

      {/* 导入钱包模态框 */}
      <Modal
        title="导入钱包私钥"
        open={isImportModalVisible}
        onOk={handleImportWallets}
        onCancel={() => {
          setIsImportModalVisible(false);
          setPrivateKeysInput('');
        }}
        width={600}
        okText="确认导入"
        cancelText="取消"
      >
        <Alert
          message="安全提醒"
          description="私钥仅在本地浏览器处理，不会上传或存储到服务器。页面关闭后私钥信息将从内存中清除。"
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        <Form layout="vertical">
          <Form.Item
            label="私钥列表 (每行一个私钥)"
            required
          >
            <Input.TextArea
              value={privateKeysInput}
              onChange={(e) => setPrivateKeysInput(e.target.value)}
              placeholder="请粘贴私钥，每行一个&#10;例如：&#10;0x1234567890abcdef...&#10;0xabcdef1234567890..."
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 安全提示 */}
      <Card size="small" style={{
        background: '#fff2f0',
        border: '1px solid #ffccc7',
        marginTop: '20px'
      }}>
        <div style={{ color: '#ff4d4f' }}>
          <strong>⚠️ 安全提醒：</strong>
          <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
            <li>请确保授权给的地址是安全可信的，授权后该地址可以使用您的代币</li>
            <li>建议在测试网络上先进行测试，确认功能正常后再在主网使用</li>
            <li>私钥信息仅在本地处理，不会上传到服务器</li>
            <li>请妥善保管您的私钥，任何人获得私钥都可以控制您的钱包</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default BatchApproval;
